import { BillingApi, Configuration } from '@/api';
import { getBillingDetailsQueryKey } from '@/api/queries/useGetBillingDetailsQuery';
import { getBillingInfoQueryKey } from '@/api/queries/useGetBillingInfoQuery';
import { BillingCustomerTarget, BillingDetails, BillingInfo } from '@/models/Billing';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';

const updateCachedBillingInfoWithSavedDetails = (
  queryClient: QueryClient,
  customer: BillingCustomerTarget | undefined,
  billingDetails: BillingDetails,
) => {
  queryClient.setQueryData<BillingInfo | undefined>(
    [getBillingInfoQueryKey[0], customer ?? null],
    (billingInfo) => (billingInfo ? { ...billingInfo, billingDetails } : billingInfo),
  );
};

export default function useUpdateBillingDetailsMutation(customer?: BillingCustomerTarget) {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const api = new BillingApi(new Configuration(auth));

  return useMutation({
    mutationFn: async (details: BillingDetails) => {
      return api.updateBillingDetails(details, customer);
    },
    onSuccess: async (billingDetails) => {
      updateCachedBillingInfoWithSavedDetails(queryClient, customer, billingDetails);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [getBillingInfoQueryKey[0], customer ?? null] }),
        queryClient.invalidateQueries({
          queryKey: [getBillingDetailsQueryKey[0], customer ?? null],
        }),
      ]);
    },
  });
}
